import React, { useState, useEffect } from 'react';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatArrow,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Badge,
  Progress,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../services/api';

const DashboardPage = () => {
  const [counts, setCounts] = useState({
    series: 0,
    subjects: 0,
    classes: 0,
    books: 0,
    bookTypes: 0,
  });
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue('white', 'gray.800');

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [series, subjects, classes, books, bookTypes] = await Promise.all([
        api.getSeries(),
        api.getSubjects(),
        api.getClasses(),
        api.getBooks(),
        api.getBookTypes(),
      ]);
      
      setCounts({
        series: series.length,
        subjects: subjects.length,
        classes: classes.length,
        books: books.length,
        bookTypes: bookTypes.length,
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  if (loading) {
    return (
      <VStack spacing={6} align="stretch">
        <Text>Loading dashboard...</Text>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Heading size="xl" color="gray.700">
        📊 Dashboard Overview
      </Heading>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stat>
              <StatLabel>Series</StatLabel>
              <StatNumber>{counts.series}</StatNumber>
              <Badge colorScheme="blue">Total</Badge>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stat>
              <StatLabel>Subjects</StatLabel>
              <StatNumber>{counts.subjects}</StatNumber>
              <Badge colorScheme="green">Total</Badge>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stat>
              <StatLabel>Classes</StatLabel>
              <StatNumber>{counts.classes}</StatNumber>
              <Badge colorScheme="purple">Total</Badge>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stat>
              <StatLabel>Books</StatLabel>
              <StatNumber>{counts.books}</StatNumber>
              <Badge colorScheme="orange">Total</Badge>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Quick Actions */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Card bg={cardBg} shadow="md">
          <CardHeader>
            <Heading size="md">Quick Actions</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Button as={RouterLink} to="/series" colorScheme="blue" size="lg">
                📚 Manage Series
              </Button>
              <Button as={RouterLink} to="/subjects" colorScheme="green" size="lg">
                📝 Manage Subjects
              </Button>
              <Button as={RouterLink} to="/classes" colorScheme="purple" size="lg">
                🎓 Manage Classes
              </Button>
              <Button as={RouterLink} to="/books" colorScheme="orange" size="lg">
                📕 Manage Books
              </Button>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="md">
          <CardHeader>
            <Heading size="md">System Overview</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text>Content Progress</Text>
                  <Text color="blue.600">{counts.series + counts.subjects + counts.books}/100</Text>
                </HStack>
                <Progress value={Math.min((counts.series + counts.subjects + counts.books) / 100 * 100, 100)} colorScheme="blue" />
              </Box>
              
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text>Book Types</Text>
                  <Badge colorScheme="teal">{counts.bookTypes} types</Badge>
                </HStack>
              </Box>

              <Button as={RouterLink} to="/files" colorScheme="cyan" variant="outline">
                📁 Upload Files
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Welcome Message */}
      <Card bg={cardBg} shadow="md">
        <CardBody>
          <VStack spacing={3}>
            <Heading size="md" color="gray.700">
              Welcome to Book Management System
            </Heading>
            <Text textAlign="center" color="gray.600">
              Manage your educational content with ease. Create series, add subjects, organize classes, 
              and upload book files all in one place.
            </Text>
            <Button as={RouterLink} to="/view" colorScheme="blue" variant="outline">
              View All Content
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default DashboardPage; 